import { RouteServiceDomainContractsEnumsLegLocationType as ELegLocationTypeEnum } from '#apiSchemas';

import type { ENOPSegmentType } from '../enums';

export { RouteServiceDomainContractsEnumsLegLocationType as ELegLocationTypeEnum } from '#apiSchemas';

export type AirportLegLocationReference = BaseLegLocationReference & {
  $type?: ELegLocationReferenceTypeEnum.airport;
  locationType: ELegLocationTypeEnum.Airport;
  airportId: string;
  fboReference?: FboLegLocationReference;
};

export enum ELegLocationReferenceTypeEnum {
  airport = 'AirportLocationRequest',
  facility = 'FacilityLocationRequest',
  custom = 'CustomAddressRequest',
}
export type FboLegLocationReference = {
  fboLocationId: string;
  fboAddressId: number;
};

export enum EGroundVehicleType {
  Sedan = 'Sedan',
  SUV = 'SUV',
}

export type LegChainPosition = {
  groupId: string;
  previousLegId: string | null;
  nextLegId: string | null;
};

export type BaseRouteLeg = {
  id: string;
  legType: ERouteLegTypeEnum;
  segmentType: ENOPSegmentType;
  chainPosition: LegChainPosition;
  comments?: string;
};
export type BaseLegLocationReference = {
  $type?: ELegLocationReferenceTypeEnum;
  locationId: string;
  id?: string;
  locationType: ELegLocationTypeEnum;
};
export type BaseRouteLegData<
  T = AirportLegLocationReference,
  R = LogisticsLegResourceType,
> = BaseRouteLeg & {
  departureLocation: T;
  arrivalLocation: T;
  departureTime: string;
  arrivalTime: string;
  departureLocalTimezone: string;
  arrivalLocalTimezone: string;
  resources: R[];
  hasScs: boolean;
};
export enum ECaseResourceType {
  Employee = 'Employee',
  OCSConsole = 'OCSConsole',
  Aircraft = 'Aircraft',
  ClinicalSpecialist = 'ClinicalSpecialist',
  Surgeon = 'Surgeon',
  Crew = 'Crew',
}
export type LogisticsLegResourceType = {
  resourceId: string;
  resourceType: ECaseResourceType;
};
// Avia

export type TmdxJetAviaLeg<
  T = AirportLegLocationReference,
  R = LogisticsLegResourceType,
> = BaseRouteLegData<T, R> & {
  legType: ERouteLegTypeEnum.TmdxJetAviaLeg;
};
export type CustomCrewMember = {
  firstName: string;
  lastName: string;
  phoneNumber: string;
};
export type SupplementalLiftAviaLeg<
  T = AirportLegLocationReference,
  R = LogisticsLegResourceType,
> = BaseRouteLegData<T, R> & {
  legType: ERouteLegTypeEnum.SupplementalLiftAviaLeg;
  tailNumber?: string;
  priceInCents: number;
  firstCrewMember: CustomCrewMember;
  secondCrewMember: CustomCrewMember;
  provider: {
    providerId: string;
    providerType: ETransportProviderType.SupplementalLiftProvider;
  };
};

export type TxCenterProviderAviaLeg<
  T = AirportLegLocationReference,
  R = LogisticsLegResourceType,
> = BaseRouteLegData<T, R> & {
  legType: ERouteLegTypeEnum.TxCenterProviderAviaLeg;
  tailNumber?: string;
  firstCrewMember: CustomCrewMember;
  secondCrewMember: CustomCrewMember;
  provider: {
    providerId: string;
    providerType: ETransportProviderType.TxCenter;
  };
};

export type CommercialFlightAviaLeg<
  T = AirportLegLocationReference,
  R = LogisticsLegResourceType,
> = BaseRouteLegData<T, R> & {
  legType: ERouteLegTypeEnum.CommercialFlightAviaLeg;
  flightNumber: string;
  bookingNumber: string;
  priceInCents: number;
  sabreGroupId?: string | null;
  seatType?: string | null;
  numberOfSeats?: number | null;
  flightOperator?: string | null;
  commercialLegType: ECommercialLegType;
};

// Ground

export type NonTmdxGroundLeg<
  T = GroundLocationReference,
  R = LogisticsLegResourceType,
> = BaseRouteLegData<T, R> & {
  driver?: string;
  email?: string;
  phoneNumber?: string;
  plateNumber?: string;
  vehicleType?: EGroundVehicleType;
  reservationNumber?: string;
  pickup?: string;
  dropoff?: string;
  wait?: string;
  stay?: string;
  distanceInMeters: number;
  isFeasibleForDrive: boolean;
};
export type GroundLegLocationReference<A, L> = BaseLegLocationReference & {
  airportReference?: A | null;
  locationReference?: L | null;
};
export type FacilityLegLocationReference = BaseLegLocationReference & {
  $type?: ELegLocationReferenceTypeEnum.facility;
  locationType:
    | ELegLocationTypeEnum.Hub
    | ELegLocationTypeEnum.Hospital
    | ELegLocationTypeEnum.TxCenter
    | ELegLocationTypeEnum.HomeResidence;
};
export type GroundLegAirportLocationReference = GroundLegLocationReference<
  AirportLegLocationReference,
  null
> & {
  locationType: ELegLocationTypeEnum.Airport;
};
export type GroundLegFacilityLocationReference = GroundLegLocationReference<
  null,
  FacilityLegLocationReference
> & {
  locationType:
    | ELegLocationTypeEnum.Hub
    | ELegLocationTypeEnum.Hospital
    | ELegLocationTypeEnum.TxCenter
    | ELegLocationTypeEnum.HomeResidence;
};
export type GroundLegCustomLocationReference = GroundLegLocationReference<
  null,
  CustomAddressLegLocationReference
> & {
  locationType: ELegLocationTypeEnum.Custom;
};
export type GroundLocationReference =
  | GroundLegAirportLocationReference
  | GroundLegFacilityLocationReference
  | GroundLegCustomLocationReference;
export type TmdxAffiliateGroundLeg<
  T = GroundLocationReference,
  R = LogisticsLegResourceType,
> = NonTmdxGroundLeg<T, R> & {
  legType: ERouteLegTypeEnum.TmdxAffiliateGroundLeg;
};

export type TxCenterProviderGroundLeg<
  T = GroundLocationReference,
  R = LogisticsLegResourceType,
> = NonTmdxGroundLeg<T, R> & {
  legType: ERouteLegTypeEnum.TxCenterProviderGroundLeg;
  provider: {
    providerId: string;
    providerType: ETransportProviderType.TxCenter;
  };
};

export type CurriServiceGroundLeg<
  T = GroundLocationReference,
  R = LogisticsLegResourceType,
> = BaseRouteLegData<T, R> & {
  legType: ERouteLegTypeEnum.CurriServiceGroundLeg;
  trackingNumber?: string;
  distanceInMeters: number;
};

export type TmdxDriveAffiliateGroundLeg<
  T = GroundLocationReference,
  R = LogisticsLegResourceType,
> = NonTmdxGroundLeg<T, R> & {
  legType: ERouteLegTypeEnum.TmdxDriveAffiliateGroundLeg;
};

export type TmdxDriveTedsGroundLeg<
  T = GroundLocationReference,
  R = LogisticsLegResourceType,
> = NonTmdxGroundLeg<T, R> & {
  legType: ERouteLegTypeEnum.TmdxDriveTedsGroundLeg;
};

export type SelfServicedGroundLeg<
  T = GroundLocationReference,
  R = LogisticsLegResourceType,
> = NonTmdxGroundLeg<T, R> & {
  legType: ERouteLegTypeEnum.SelfServicedGroundLeg;
};

// Accommodations'

export type CustomAddressLegLocationReference = BaseLegLocationReference & {
  $type?: ELegLocationReferenceTypeEnum.custom;
  locationType: ELegLocationTypeEnum.Custom;
  hereMapsId: string;
};

export type AccommodationLeg<
  T = CustomAddressLegLocationReference,
  R = LogisticsLegResourceType,
> = Omit<
  BaseRouteLeg & {
    legType: ERouteLegTypeEnum.AccommodationLeg;
    checkInAt: string;
    checkOutAt: string;
    hotelName: string;
    rooms: number;
    bookingNumber?: string;
    location: T | null;
    resources: R[];
    hasScs: boolean;
  },
  'chainPosition'
>;

export enum ETransportProviderType {
  Tmdx = 'Tmdx',
  Commercial = 'Commercial',
  Partner = 'Partner',
  SupplementalLiftProvider = 'SupplementalLiftProvider',
  TMDXAffiliate = 'TMDXAffiliate',
  TxCenter = 'TxCenter',
  Curri = 'Curri',
  Teds = 'Teds',
  SelfServiced = 'SelfServiced',
}

export enum ERouteLegTypeEnum {
  AccommodationLeg = 'AccommodationLeg',
  CommercialFlightAviaLeg = 'CommercialFlightAviaLeg',
  CurriServiceGroundLeg = 'CurriServiceGroundLeg',
  SupplementalLiftAviaLeg = 'SupplementalLiftAviaLeg',
  TmdxAffiliateGroundLeg = 'TmdxAffiliateGroundLeg',
  TxCenterProviderGroundLeg = 'TxCenterProviderGroundLeg',
  TxCenterProviderAviaLeg = 'TxCenterProviderAviaLeg',
  TmdxJetAviaLeg = 'TmdxJetAviaLeg',
  TmdxDriveAffiliateGroundLeg = 'TmdxDriveAffiliateGroundLeg',
  TmdxDriveTedsGroundLeg = 'TmdxDriveTedsGroundLeg',
  SelfServicedGroundLeg = 'SelfServicedGroundLeg',
}

export enum ECommercialLegType {
  Manual = 'Manual',
  Sabre = 'Sabre',
}

export enum ESuggestedRouteLegCandidateType {
  TmdxJetAviaLegCandidateRequest = 'TmdxJetAviaLegCandidateRequest',
  TmdxAffiliateGroundLegCandidateRequest = 'TmdxAffiliateGroundLegCandidateRequest',
  CommercialFlightAviaLegCandidateRequest = 'CommercialFlightAviaLegCandidateRequest',
  AccommodationLegCandidateRequest = 'AccommodationLegCandidateRequest',
}

export enum EEventOperationType {
  NoChange = 'NoChange',
  Added = 'Added',
  Modified = 'Modified',
  Removed = 'Removed',
}

export enum ELogisticsChangeLogType {
  // "Add" leg logs
  TmdxJetAviaLegAddedLog = 'TmdxJetAviaLegAddedLog',
  SupplementalLiftAviaLegAddedLog = 'SupplementalLiftAviaLegAddedLog',
  TxCenterProviderAviaLegAddedLog = 'TxCenterProviderAviaLegAddedLog',
  CommercialFlightAviaLegAddedLog = 'CommercialFlightAviaLegAddedLog',
  SabreCommercialAviaLegsAddedLog = 'SabreCommercialAviaLegsAddedLog',
  AccommodationLegAddedLog = 'AccommodationLegAddedLog',
  TmdxAffiliateGroundLegAddedLog = 'TmdxAffiliateGroundLegAddedLog',
  TxCenterProviderGroundLegAddedLog = 'TxCenterProviderGroundLegAddedLog',
  CurriServiceGroundLegAddedLog = 'CurriServiceGroundLegAddedLog',
  // "Modify" leg logs
  TmdxJetAviaLegModifiedLog = 'TmdxJetAviaLegModifiedLog',
  SupplementalLiftAviaLegModifiedLog = 'SupplementalLiftAviaLegModifiedLog',
  TxCenterProviderAviaLegModifiedLog = 'TxCenterProviderAviaLegModifiedLog',
  CommercialFlightAviaLegModifiedLog = 'CommercialFlightAviaLegModifiedLog',
  SabreCommercialAviaLegModifiedLog = 'SabreCommercialAviaLegModifiedLog',
  TmdxAffiliateGroundLegModifiedLog = 'TmdxAffiliateGroundLegModifiedLog',
  TxCenterProviderGroundLegModifiedLog = 'TxCenterProviderGroundLegModifiedLog',
  CurriServiceGroundLegModifiedLog = 'CurriServiceGroundLegModifiedLog',
  AccommodationLegModifiedLog = 'AccommodationLegModifiedLog',
  // "Remove" leg logs
  TmdxJetAviaLegRemovedLog = 'TmdxJetAviaLegRemovedLog',
  SupplementalLiftAviaLegRemovedLog = 'SupplementalLiftAviaLegRemovedLog',
  TxCenterProviderAviaLegRemovedLog = 'TxCenterProviderAviaLegRemovedLog',
  CommercialFlightAviaLegRemovedLog = 'CommercialFlightAviaLegRemovedLog',
  SabreCommercialAviaGroupRemovedLog = 'SabreCommercialAviaGroupRemovedLog',
  TmdxAffiliateGroundLegRemovedLog = 'TmdxAffiliateGroundLegRemovedLog',
  TxCenterProviderGroundLegRemovedLog = 'TxCenterProviderGroundLegRemovedLog',
  CurriServiceGroundLegRemovedLog = 'CurriServiceGroundLegRemovedLog',
  AccommodationLegRemovedLog = 'AccommodationLegRemovedLog',
  // "Hub" logs
  RouteDepartureHubAssignedLog = 'RouteDepartureHubAssignedLog',
  RouteDepartureHubUnassignedLog = 'RouteDepartureHubUnassignedLog',
  RouteArrivalHubAssignedLog = 'RouteArrivalHubAssignedLog',
  RouteArrivalHubUnassignedLog = 'RouteArrivalHubUnassignedLog',
  // Route logs
  RouteRemovedLog = 'RouteRemovedLog',
  // Resource logs
  LogisticsResourceSwappedLog = 'LogisticsResourceSwappedLog',
  LogisticsResourceRemovedLog = 'LogisticsResourceRemovedLog',
}

export enum ERemovedLegTypeEnum {
  AccommodationLegLog = 'AccommodationLegLog',
  AviaLegLog = 'AviaLegLog',
  GroundLegLog = 'GroundLegLog',
}

export enum EModifiedResourceTypeEnum {
  AircraftLogResourceChanges = 'AircraftLogResourceChanges',
  CrewLogResourceChanges = 'CrewLogResourceChanges',
  OcsConsoleLogResourceChanges = 'OcsConsoleLogResourceChanges',
  EmployeeLogResourceChanges = 'EmployeeLogResourceChanges',
}

export type GroundLegTypes =
  | TmdxAffiliateGroundLeg
  | TxCenterProviderGroundLeg
  | CurriServiceGroundLeg
  | TmdxDriveAffiliateGroundLeg
  | TmdxDriveTedsGroundLeg
  | SelfServicedGroundLeg;
export type AviaLegTypes =
  | TmdxJetAviaLeg
  | SupplementalLiftAviaLeg
  | TxCenterProviderAviaLeg
  | CommercialFlightAviaLeg;

export type RouteLegType = AviaLegTypes | GroundLegTypes | AccommodationLeg;
